import slugify from '@sindresorhus/slugify'
import clsx from 'clsx'
import NextImage from 'next/legacy/image'
import Link from 'next/link'
import { type FunctionComponent, type ReactNode, useEffect, useState } from 'react'
import { useIsAllowedToFullUse } from '../../../hooks/useIsAllowedToFullUse'
import { getDailyDosePrice, getDefinedDailyDoseOfProduct } from '../../const/DailyEnergyRequirementsHelper'
import { InternalLink } from '../../const/Links'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import type { ItemSubCategoryResult } from '../../data/ItemSubCategoryFragment'
import type { ProductBannerResult } from '../../data/ProductFragment'
import SvgChevronDown from '../../svg/icons/ChevronDown'
import { useActiveDog } from '../../user/ActiveDogContext'
import { useAuth } from '../../user/UserContext'
import { Button } from '../shared/Button'
import { CompareButton } from '../shared/CompareButton'
import { CustomTooltip } from '../shared/CustomTooltip'
import { EmptyAllergens } from '../shared/EmptyAllergens'
import { HeurekaButton } from '../shared/HeurekaButton'
import { Hidden } from '../shared/Hidden'
import { LoaderBannerDailyDose } from '../shared/LoaderBannerDailyDose'
import { Message } from '../shared/Message'
import { NotSpecifiedAlert } from '../shared/NotSpecifiedAlert'
import { Rating } from '../shared/Rating'
import { Tag } from '../shared/Tag'
import style from './ProductBanner.module.sass'

type ProductBannerProps = {
	product: ProductBannerResult
	excludedAllergens?: ItemSubCategoryResult[]
	infoBarContent?: ReactNode
	isHighlighted?: 'green' | 'red'
	badge?: string
	badgeColor?: string
	isTop?: boolean
	openModal: (() => void) | undefined
	isTopProduct?: boolean
	noBottomBorder?: boolean
	isTagHidden?: boolean
	dogDailyEnergyRequirement: number
	buttonsRelocated?: boolean
	isHeadlineProduct?: boolean
	isEmbedded?: boolean
	position?: number
	missingComposition?: boolean
	handleDataLayersOnProductClick?: () => void
	showDetailButton?: boolean
}

export const ProductBanner: FunctionComponent<ProductBannerProps> = ({
	product,
	infoBarContent,
	excludedAllergens,
	buttonsRelocated = false,
	isHighlighted,
	noBottomBorder = false,
	openModal,
	isTopProduct = false,
	isTop = false,
	isTagHidden = false,
	isHeadlineProduct,
	isEmbedded = false,
	badge,
	badgeColor,
	position,
	missingComposition,
	dogDailyEnergyRequirement,
	handleDataLayersOnProductClick,
	showDetailButton = false,
}) => {
	const translate = useTranslate()
	const auth = useAuth()
	const { dog } = useActiveDog()

	const [isDetailVisible, setIsDetailVisible] = useState(false)
	const isAllowedToFullUse = useIsAllowedToFullUse()

	const loading = auth.status === 'loading'

	const commonAllergensOfProduct = product.itemSubCategories.filter(
		(subCategory) => subCategory.isMostCommonAllergenTag,
	)
	const isFedProduct = dog?.fedProduct === product.id
	const productPositionIndex = typeof position === 'number' && position >= 0 ? position + 1 : undefined
	const topThreeIndex =
		typeof productPositionIndex === 'number' && productPositionIndex <= 3 ? productPositionIndex : undefined

	const isTopThree = Boolean(topThreeIndex) || isTop
	const isTopFirst = isTopProduct || productPositionIndex === 1

	const labelIsFed = productPositionIndex
		? `${translate('productBanner.tag.fedProduct')}, ${productPositionIndex}.`
		: translate('productBanner.tag.fedProduct')

	const label = topThreeIndex
		? `${translate('productIntroSummary.tag.top')} ${topThreeIndex}`
		: `${productPositionIndex}.`

	const userAllowedToFullUse = useIsAllowedToFullUse()

	const imageAltText = product.productLine
		? `${product.brand?.name} ${product.productLine.name} ${product.name}`
		: `${product.brand?.name} ${product.name}`

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const savedState = localStorage.getItem(`detailOpen_${product.slug}`)
			if (savedState === 'true') {
				setIsDetailVisible(true)
			}
		}
	}, [product.slug])

	const toggleDetail = () => {
		const newState = !isDetailVisible
		setIsDetailVisible(newState)
		if (typeof window !== 'undefined') {
			localStorage.setItem(`detailOpen_${product.slug}`, newState ? 'true' : 'false')
			if (newState) {
				const detailElement = document.getElementById(`${slugify(product.name)}-detail`)
				if (detailElement) {
					detailElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
				}
			}
		}
	}

	return (
		<div
			className={clsx(
				style.articleWrapper,
				isEmbedded && style.articleWrapperEmbedded,
				isEmbedded && badge && style.hasBadge,
				isHighlighted && style[`article_wrapper_${isHighlighted}`],
				isHighlighted && style.articleWrapperHighlighted,
				!isHeadlineProduct && infoBarContent && style.smallBottomPadding,
				noBottomBorder && style.noBottomBorder,
			)}
		>
			{badge && (
				<div
					className={clsx(style.badge)}
					style={{
						backgroundColor: badgeColor,
					}}
				>
					{badge}
				</div>
			)}
			<div className={style.article}>
				<div className={style.thumbnail}>
					{((productPositionIndex && (userAllowedToFullUse || isTopFirst || isFedProduct)) || isFedProduct) &&
					!isTagHidden ? (
						<div className={style.order}>
							<Tag
								color={isTopThree || isTopFirst ? 'greenLight' : isFedProduct ? 'pink' : 'grey'}
								isLarger
								label={isFedProduct ? labelIsFed : label}
							/>
						</div>
					) : undefined}
					<Link
						onClick={() => {
							handleDataLayersOnProductClick && handleDataLayersOnProductClick()
						}}
						href={`${InternalLink.Product}${product.slug}`}
						title={`${product.brand?.name} ${product.productLine?.name}`}
					>
						<div className={style.imageWrapper}>
							<NextImage
								src={product.heurekaImage?.url ?? product.image?.url ?? '/images/placeholder-product.png'}
								alt={imageAltText}
								title={imageAltText}
								layout="fill"
								objectFit="contain"
								sizes="100px"
							/>
						</div>
					</Link>
					<>
						<Hidden breakpoint="mobileDown">
							<CompareButton id={product.id} openModal={openModal} />
						</Hidden>
						<Hidden breakpoint="mobileUp">
							<div className={style.headerMobile}>
								<Link
									onClick={() => {
										handleDataLayersOnProductClick && handleDataLayersOnProductClick()
									}}
									href={`${InternalLink.Product}${product.slug}`}
									title={`${product.brand?.name} ${product.productLine?.name}`}
								>
									{isHeadlineProduct ? (
										<h2 className={style.noMargin}>
											<span className={style.title}>
												{product.brand?.name} {product.productLine?.name}
											</span>{' '}
											<div className={style.paragraph}>{product.name}</div>
										</h2>
									) : (
										<h3 className={style.noMargin}>
											<span className={style.title}>
												{product.brand?.name} {product.productLine?.name}
											</span>{' '}
											<div className={style.paragraph}>{product.name}</div>
										</h3>
									)}
								</Link>
							</div>
						</Hidden>
					</>
				</div>
				<div className={style.body}>
					<Hidden breakpoint="mobileDown">
						<Link
							onClick={() => {
								handleDataLayersOnProductClick && handleDataLayersOnProductClick()
							}}
							href={`${InternalLink.Product}${product.slug}`}
							title={`${product.brand?.name} ${product.productLine?.name}`}
						>
							{isHeadlineProduct ? (
								<h2 className={style.noMargin}>
									<span className={style.title}>
										{product.brand?.name} {product.productLine?.name}
									</span>{' '}
									<div className={style.paragraph}>{product.name}</div>
								</h2>
							) : (
								<h3 className={style.noMargin}>
									<span className={style.title}>
										{product.brand?.name} {product.productLine?.name}
									</span>{' '}
									<div className={style.paragraph}>{product.name}</div>
								</h3>
							)}
						</Link>
					</Hidden>
					{product.detailedComposition && product.detailedComposition !== '0' ? (
						<div className={style.additionalInfoWrapper}>
							<div className={style.additionalInfoIcons}>
								{excludedAllergens?.map((allergen) => (
									<span key={allergen.id}>
										<span className={style.iconWrapper} data-tooltip-id={`tooltipIconMobile_${allergen.id}`} data-tip>
											{allergen.iconNegative?.url && (
												<NextImage
													src={allergen.iconNegative.url}
													alt={translate('alternativeText.icon')}
													title={allergen.negativeWording}
													layout="fill"
													objectFit="contain"
													sizes="36px"
												/>
											)}
										</span>
										<Hidden breakpoint="mobileUp">
											<CustomTooltip
												tooltipId={`tooltipIconMobile_${allergen.id}`}
												textAfterHover={allergen.negativeWording}
											/>
										</Hidden>
									</span>
								))}
							</div>
							{commonAllergensOfProduct.length === 0 ? (
								<span className={style.additionalInfoText}>
									<Translation tr="productBanner.mostCommonAllergensExcluded" />
								</span>
							) : (
								<span className={style.additionalInfoText}>
									<Translation tr="productBanner.mostCommonAllergensContained" />{' '}
									{commonAllergensOfProduct.map((allergen, order) =>
										order === commonAllergensOfProduct.length - 1
											? allergen.name.toLocaleLowerCase()
											: `${allergen.name.toLocaleLowerCase()}, `,
									)}
								</span>
							)}
							{showDetailButton && (
								<div className={style.detailButtonWrapper}>
									<Button
										type={{ variant: 'naked' }}
										label={translate('fullProduct.readMore')}
										iconAfter={
											<span className={clsx(style.icon, isDetailVisible && style.iconRotate)}>
												<SvgChevronDown />
											</span>
										}
										icon={{ fillPrimary: true }}
										onClick={toggleDetail}
									/>
								</div>
							)}
						</div>
					) : missingComposition && (typeof product.score !== 'number' || product.score === -1) ? (
						''
					) : (
						<EmptyAllergens />
					)}
				</div>
				<div className={clsx(style.rightContent, typeof product.score !== 'number' && style.rightContentBottomed)}>
					<div className={clsx(style.ratingWrapper, typeof product.score !== 'number' && style.ratingWrapperEmpty)}>
						<Rating rateAmount={product.score} />
					</div>

					<div>
						{loading ? (
							<LoaderBannerDailyDose />
						) : (
							<>
								{dogDailyEnergyRequirement ? (
									isAllowedToFullUse || productPositionIndex === 1 || isFedProduct || isTopProduct ? (
										<div className={style.guidelineDailyAmountWrapper}>
											<span className={style.titleGuidelineDailyAmount}>
												<Translation tr="productBanner.dailyDose" />
											</span>{' '}
											{typeof product.metabolizedEnergy === 'number' && product.metabolizedEnergy >= 0 ? (
												<>
													<div className={style.guidelineDailyAmount}>
														{getDefinedDailyDoseOfProduct(dogDailyEnergyRequirement, product.metabolizedEnergy)}{' '}
														<Translation tr="dose.gram.shortcut" />{' '}
														{product.packagings && product.packagings.length > 0 && (
															<span>
																<Translation tr="productBanner.from" />{' '}
																{product.packagings &&
																getDailyDosePrice(dogDailyEnergyRequirement, product.czkPerMetabolizedEnergy) === 0
																	? 1
																	: getDailyDosePrice(dogDailyEnergyRequirement, product.czkPerMetabolizedEnergy)}
																&nbsp;
																<Translation tr="productBanner.currency" />
															</span>
														)}
													</div>
												</>
											) : (
												<>
													<div className={style.messageEmpty}>
														<Message
															icon={<span className={style.infoIcon}>i</span>}
															text={
																<>
																	<Translation tr="productBanner.message.missingInfo" />
																</>
															}
														/>
													</div>
												</>
											)}
										</div>
									) : (
										<NotSpecifiedAlert color="grey" notSigned />
									)
								) : (
									<NotSpecifiedAlert color="grey" />
								)}
							</>
						)}
						{!buttonsRelocated && (
							<div className={clsx(style.buttonWrapper)}>
								<Button
									type={{ variant: 'primary' }}
									label={translate('productBanner.button.detail')}
									url={`${InternalLink.Product}${product.slug}`}
									onClick={() => {
										handleDataLayersOnProductClick && handleDataLayersOnProductClick()
									}}
								/>
								<HeurekaButton
									variant="bordered"
									id={product.packagings[0].id}
									link={product.packagings[0]?.heurekaLink}
								/>
							</div>
						)}
						<Hidden breakpoint="mobileUp">
							<CompareButton id={product.id} openModal={openModal} />
						</Hidden>
					</div>
				</div>
			</div>
			{infoBarContent && <div className={style.infoBarGroup}>{infoBarContent}</div>}
		</div>
	)
}
